import React, { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { showUpgradeModal } from '../../redux/slices/tierSlice';
import { isNative, isIOS } from '../../utils/webOnly';
import './TrialBanner.scss';

const TrialBanner = () => {
    const dispatch = useDispatch();
    const reason = useSelector((state) => state.tier.reason);
    const effectiveTier = useSelector((state) => state.tier.effectiveTier);
    const daysRemaining = useSelector((state) => state.tier.trialDaysRemaining);
    const isTrialActive = useSelector((state) => state.tier.isTrialActive);
    const isTrialExpired = useSelector((state) => state.tier.isTrialExpired);
    const isLoaded = useSelector((state) => state.tier.isLoaded);
    const trialStartDate = useSelector((state) => state.tier.trialStartDate);
    const user = useSelector((state) => state.auth.user);

    const hadServerTrial = !!trialStartDate;

    // On iOS we only surface: (a) the countdown for an active SERVER trial
    // (reason 'trial' — e.g. one started on desktop, honored here), and (b) a
    // "start your free trial" prompt for trial-eligible users (no prior server
    // trial) so dismissing the paywall doesn't lose the trial entry point. The
    // generic free/expired "upgrade" nag stays hidden on iOS. Apple StoreKit
    // trials are reason 'iap' → handled by Apple, no badge. Web/desktop/Android
    // keep the full behavior.
    const shouldShow = isLoaded && reason !== 'subscription' && reason !== 'iap' && (
        isIOS
            ? ((isTrialActive && reason === 'trial') || (effectiveTier === 'free' && !hadServerTrial))
            : (isTrialExpired || isTrialActive || effectiveTier === 'free')
    );

    // Add class to document so fixed-positioned elements can offset
    useEffect(() => {
        if (shouldShow) {
            document.documentElement.classList.add('has-trial-banner');
        } else {
            document.documentElement.classList.remove('has-trial-banner');
        }
        return () => document.documentElement.classList.remove('has-trial-banner');
    }, [shouldShow]);

    const handleUpgrade = useCallback(() => {
        window.electron.analytics.track('subscribe_clicked', { source: 'trial_banner' });
        // On native (iOS App Store / Android Play): open the in-app paywall
        // that routes through Apple/Google IAP. Steering to the web checkout
        // from a native app is an Apple 3.1.1 violation AND skips Play
        // Billing — both stores can pull the app for it. The MobilePaywall
        // component handles purchase + restore + Terms/Privacy links.
        if (isNative) {
            dispatch(showUpgradeModal());
            return;
        }
        const params = new URLSearchParams();
        if (user?.id) params.set('userId', user.id);
        if (user?.email) params.set('email', user.email);
        params.set('feature', 'trial_banner');
        const checkoutUrl = `https://mixgoat.com/checkout${params.toString() ? '?' + params.toString() : ''}`;
        window.electron.openUrl(checkoutUrl);
    }, [user, dispatch]);

    if (!isLoaded) return null;

    // Never show for paying subscribers (Stripe or IAP)
    if (reason === 'subscription' || reason === 'iap') return null;

    // iOS: countdown for an active server trial, or a start-trial prompt for
    // eligible users — nothing else.
    if (isIOS) {
        if (isTrialActive && reason === 'trial') {
            return (
                <div className="trial-banner trial-banner-active">
                    <span className="trial-banner-text">
                        GOAT Trial — <strong>{daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}</strong> remaining
                    </span>
                    <button className="trial-banner-btn" onClick={handleUpgrade}>
                        Upgrade Now
                    </button>
                </div>
            );
        }
        if (effectiveTier === 'free' && !hadServerTrial) {
            return (
                <div className="trial-banner trial-banner-free">
                    <span className="trial-banner-text">
                        Start your 14-day free trial — unlock all of GOAT
                    </span>
                    <button className="trial-banner-btn" onClick={handleUpgrade}>
                        Start Free Trial
                    </button>
                </div>
            );
        }
        return null;
    }

    // Trial expired state
    if (isTrialExpired) {
        return (
            <div className="trial-banner trial-banner-expired">
                <span className="trial-banner-text">
                    Your free trial has ended — upgrade to keep using premium features
                </span>
                <button className="trial-banner-btn" onClick={handleUpgrade}>
                    Upgrade Now
                </button>
            </div>
        );
    }

    // Active trial state
    if (isTrialActive) {
        return (
            <div className="trial-banner trial-banner-active">
                <span className="trial-banner-text">
                    GOAT Trial — <strong>{daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}</strong> remaining
                </span>
                <button className="trial-banner-btn" onClick={handleUpgrade}>
                    Upgrade Now
                </button>
            </div>
        );
    }

    // Free user (trial already used)
    if (effectiveTier === 'free') {
        return (
            <div className="trial-banner trial-banner-free">
                <span className="trial-banner-text">
                    You're on the free plan — unlock all premium features with GOAT
                </span>
                <button className="trial-banner-btn" onClick={handleUpgrade}>
                    Upgrade Now
                </button>
            </div>
        );
    }

    return null;
};

export default TrialBanner;
